import { Flex, Select } from "antd";
import React, { useCallback } from "react";
import { useState } from "react";
import { CustomInputNumber } from "~/components";
import { convertDuration, durationToDisplay } from "~/utils";
import { Duration, TimeDuration } from "~/types";

const defaultChoices = [Duration.Minute, Duration.Hour, Duration.Day];

type DurationPickerProps = {
  value?: string;
  onChange?: (count: string) => void;
  min?: TimeDuration;
  max?: TimeDuration;
  choicesOf?: Duration[];
  className?: string;
};

export const DurationPicker = ({
  value,
  onChange,
  min,
  max,
  choicesOf: choices,
  className,
}: DurationPickerProps) => {
  if (!choices || choices.length === 0) choices = defaultChoices;

  const split = value?.split(" ") ?? ["0", choices[0]];

  const [num, setNum] = useState(Number(split[0]));
  const [of, setOf] = useState(split[1][0] as Duration);

  const invokeChange = useCallback(() => {
    if (!onChange) return;
    onChange(`${num} ${of}`);
  }, [num, of, onChange]);

  React.useEffect(() => {
    invokeChange();
  }, [num, of, invokeChange]);

  const chooseNum = (newNum: number | null) => {
    setNum(newNum ?? 0);
  };

  const chooseOf = (newOf: Duration) => {
    setOf(newOf);
    if (min) {
      const newMin = Math.ceil(convertDuration(min, newOf) ?? 1);
      if (num < newMin) setNum(newMin);
    }
    if (max) {
      const newMax = Math.ceil(convertDuration(max, newOf) ?? 1);
      if (num > newMax) setNum(newMax);
    }
  };

  return (
    <Flex className={className}>
      <CustomInputNumber
        min={min ? Math.ceil(convertDuration(min, of) ?? 1) : undefined}
        max={max ? Math.ceil(convertDuration(max, of) ?? 1) : undefined}
        value={num}
        whole
        nonNegative
        onChange={chooseNum}
        style={{
          width: "100%",
        }}
      />
      <Select onChange={chooseOf} value={of}>
        {choices.map((value) => {
          const display = durationToDisplay(value);
          return (
            <Select.Option key={value} value={value} label={display}>
              <div title={display}>{display}(s)</div>
            </Select.Option>
          );
        })}
      </Select>
    </Flex>
  );
};
